/**
 * The system's money, and the rate it may be read in dollars at. The lab's
 * request of 17 October 2026, item 13: "currency of the system make it in AED,
 * and allow exchange manually to dollars, exchange rate will be entered
 * manually by accountant."
 *
 * Every amount is kept and typed in dirhams. The rate is how many dirhams one
 * dollar buys, set by hand by a holder of pay, and only ever used to SHOW an
 * amount in dollars to whoever asks for that. The API's ExchangeRates, in the
 * same words.
 */
export const CURRENCY = 'AED';
export const FOREIGN_CURRENCY = 'USD';
export type Currency = typeof CURRENCY | typeof FOREIGN_CURRENCY;
export const CURRENCIES: readonly Currency[] = [CURRENCY, FOREIGN_CURRENCY];

export function isCurrency(value: unknown): value is Currency {
  return value === CURRENCY || value === FOREIGN_CURRENCY;
}

/** The most a rate may be: far above any the dirham has had, and a typo's ceiling. */
export const EXCHANGE_RATE_MAX = 1000;

export const EXCHANGE_RATE_REFUSAL = {
  badRate: 'The rate is how many dirhams one dollar buys: a number above zero, up to six decimal places.',
  noPermission: 'You do not have permission to set the exchange rate.',
} as const;

/** Whether a number has no more decimal places than these. */
function placesAtMost(value: number, places: number): boolean {
  const scale = 10 ** places;
  return Math.round(value * scale) / scale === value;
}

/** Why a rate cannot be kept, or null when it can. */
export function exchangeRateRefusal(rate: number | null | undefined): string | null {
  if (rate == null || !Number.isFinite(rate) || rate <= 0 || rate > EXCHANGE_RATE_MAX) {
    return EXCHANGE_RATE_REFUSAL.badRate;
  }
  return placesAtMost(rate, 6) ? null : EXCHANGE_RATE_REFUSAL.badRate;
}

/** An amount of dirhams in dollars at a rate, to the cent. */
export function inDollars(dirhams: number, rate: number): number {
  return Math.round((dirhams / rate) * 100) / 100;
}

/**
 * Doctors' own prices: item 11 of the same request. "a general items price
 * list which applies to everyone, and then accountant can open each doctors
 * page and edit its item prices. If the price changed, in the past cases the
 * prices should stay the same, and only in the future cases the price should
 * change." A case keeps the prices it was taken in at; what is new is where a
 * price comes from when it is taken: the doctor's own, else the list's. The
 * API's DoctorPrices, in the same words.
 */
export const DOCTOR_PRICE_MAX = 1_000_000;

export const DOCTOR_PRICE_REFUSAL = {
  badPrice: 'A price is a number from 0 up, with at most two decimal places.',
  notAnItem: 'That is not an item of the price list.',
  noPermission: 'You do not have permission to change prices.',
} as const;

/** Why a price cannot be kept, or null when it can. */
export function doctorPriceRefusal(price: number | null | undefined): string | null {
  if (price == null || !Number.isFinite(price) || price < 0 || price > DOCTOR_PRICE_MAX) {
    return DOCTOR_PRICE_REFUSAL.badPrice;
  }
  return placesAtMost(price, 2) ? null : DOCTOR_PRICE_REFUSAL.badPrice;
}

/**
 * The price a case takes for an item: the doctor's own where one is set, the
 * list's otherwise. Null for an item with no price on the list.
 */
export function priceForDoctor(
  item: { id: string; unitPrice?: number | null },
  doctorPrices: Readonly<Record<string, number>>,
): number | null {
  const own = doctorPrices[item.id];
  return own ?? item.unitPrice ?? null;
}
